import axios, { AxiosResponse } from 'axios';
import type {
  BiPanelHistoryEntry,
  BiPanelParameter,
  BiPanelParameterCreatePayload,
  BiPanelParameterUpdatePayload,
  BiPanelRule,
  BiPanelRuleCreatePayload,
  BiPanelRuleListParams,
  BISelectOption,
} from '@/store/modules/bi-panel-rule/bi-panel-rule-types';
import HttpService from '@/services-http/HttpService';
import FinancialGroupService from '@/services-http/contract/FinancialGroupService';
import ContractService from '@/services-http/contract/ContractService';
import CarrierService from '@/services-http/contract/CarrierService';
import { AuthenticationService } from './authentication-service';

const ENDPOINT = '/bi-panel-rule';
const PARAMETER_ENDPOINT = '/bi-panel-parameter';

const BI_PANEL_RULE_LIST_LEVEL = 'CARRIER';

// Mesmo cliente das demais telas que consomem o analytics-bff (cookie + X-Sdi-App-Origin); o axios puro causava HTTP 500 no combo de Grupo Financeiro
const analyticsBffApi = new HttpService('/analytics-bff');

// Os endpoints do sdi-service exigem a authority 'server' e só aceitam chamada via sdi-bff-service
const sdiBffApi = axios.create({ baseURL: `${process.env.VUE_APP_API}/sdi-bff`, withCredentials: true });
sdiBffApi.interceptors.request.use(AuthenticationService.requestTokenInterceptor);
sdiBffApi.interceptors.response.use(
  (success) => success,
  AuthenticationService.errorResponseInterceptor,
);

type BiPanelRuleSpringPage<T> = {
  content: T[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
  last?: boolean;
  // Spring Data 3.3+ (PagedModel via DTO) move os metadados para `page`
  page?: { number: number; size: number; totalElements: number; totalPages: number };
};

export type BiPanelRulePage<T> = {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

type ContractEntity = {
  id: number;
  policy?: string;
  benefit_id?: { name?: string } | string | null;
  carrier_id?: { xipp_commercial_name?: string } | null;
  contract_owner_id?: { legal_name?: string } | null;
};

export default class BiPanelRuleService {
  async fetchFinancialGroupOptions(
    page: number,
    size: number,
    searchText: string,
  ): Promise<AxiosResponse<{ content: BISelectOption[]; last: boolean }>> {
    const query: Record<string, string | number> = { page, size };

    if (searchText) query.name = searchText;

    return analyticsBffApi.get('/financial-group/paginated', query);
  }

  async fetchCarrierOptions(
    page: number,
    size: number,
    searchText: string,
  ): Promise<AxiosResponse<{ content: BISelectOption[]; last: boolean }>> {
    const query: Record<string, string | number> = { page, size };

    if (searchText) query.name = searchText;

    return analyticsBffApi.get('/carrier/paginated', query);
  }

  async fetchContractOptions(
    page: number,
    size: number,
    searchText: string,
    financialGroupId: number | null,
  ): Promise<AxiosResponse<{ content: BISelectOption[]; last: boolean }>> {
    const query: Record<string, string | number> = { page, size };

    if (searchText) query.searchByLegalNameOrPolicy = searchText;
    if (financialGroupId) query.financialGroupId = financialGroupId;

    return analyticsBffApi.get('/contract/paginated', query);
  }

  async fetchRules(params: BiPanelRuleListParams): Promise<BiPanelRulePage<BiPanelRule>> {
    const response = await sdiBffApi.get<BiPanelRuleSpringPage<BiPanelRule> | BiPanelRule[]>(
      ENDPOINT,
      { params: this.buildListQueryParams(params) },
    );
    const { data } = response;

    if (Array.isArray(data)) {
      return {
        content: data,
        page: params.page,
        size: params.size,
        totalElements: data.length,
        totalPages: data.length ? 1 : 0,
      };
    }

    if (!data || !Array.isArray(data.content)) {
      return {
        content: [],
        page: params.page,
        size: params.size,
        totalElements: 0,
        totalPages: 0,
      };
    }

    const meta = data.page;

    return {
      content: data.content,
      page: meta?.number ?? data.number ?? params.page,
      size: meta?.size ?? data.size ?? params.size,
      totalElements: meta?.totalElements ?? data.totalElements ?? data.content.length,
      totalPages: meta?.totalPages ?? data.totalPages ?? 1,
    };
  }

  async fetchRule(ruleId: number): Promise<BiPanelRule> {
    const response = await sdiBffApi.get<BiPanelRule>(`${ENDPOINT}/${ruleId}`);

    return response.data;
  }

  async createRule(payload: BiPanelRuleCreatePayload): Promise<BiPanelRule> {
    const response = await sdiBffApi.post<BiPanelRule>(ENDPOINT, payload);

    return response.data;
  }

  async addParameter(ruleId: number, payload: BiPanelParameterCreatePayload): Promise<BiPanelParameter> {
    const response = await sdiBffApi.post<BiPanelParameter>(`${ENDPOINT}/${ruleId}/parameter`, payload);

    return response.data;
  }

  async updateRuleStatus(ruleId: number, active: boolean): Promise<BiPanelRule> {
    const response = await sdiBffApi.put<BiPanelRule>(`${ENDPOINT}/${ruleId}/status`, { active });

    return response.data;
  }

  async updateParameter(parameterId: number, payload: BiPanelParameterUpdatePayload): Promise<BiPanelParameter> {
    const response = await sdiBffApi.put<BiPanelParameter>(`${PARAMETER_ENDPOINT}/${parameterId}`, payload);

    return response.data;
  }

  async updateParameterStatus(parameterId: number, active: boolean): Promise<BiPanelParameter> {
    const response = await sdiBffApi.put<BiPanelParameter>(`${PARAMETER_ENDPOINT}/${parameterId}/status`, { active });

    return response.data;
  }

  async fetchRuleHistory(ruleId: number): Promise<BiPanelHistoryEntry> {
    const response = await sdiBffApi.get<BiPanelHistoryEntry>(`${ENDPOINT}/${ruleId}/history`);

    return response.data;
  }

  async fetchParameterHistory(parameterId: number): Promise<BiPanelHistoryEntry> {
    const response = await sdiBffApi.get<BiPanelHistoryEntry>(`${PARAMETER_ENDPOINT}/${parameterId}/history`);

    return response.data;
  }

  async resolveFinancialGroupNames(ids: number[]): Promise<Record<number, string>> {
    if (!ids.length) return {};

    const financialGroupService = new FinancialGroupService();
    const response = await financialGroupService.FindByIds(ids);
    const items: Array<{ id: number; name?: string }> = response?.data || [];

    return items.reduce((acc: Record<number, string>, item) => {
      acc[item.id] = item.name || '';
      return acc;
    }, {});
  }

  async resolveCarrierNames(ids: number[]): Promise<Record<number, string>> {
    if (!ids.length) return {};

    const carrierService = new CarrierService();
    const response = await carrierService.FindByIds(ids.join(','));
    const items: Array<{ id: number; xipp_commercial_name?: string; name?: string }> = response?.data || [];

    return items.reduce((acc: Record<number, string>, item) => {
      acc[item.id] = item.xipp_commercial_name || item.name || '';
      return acc;
    }, {});
  }

  async resolveContractNames(ids: number[]): Promise<Record<number, string>> {
    if (!ids.length) return {};

    const contractService = new ContractService();
    const response = await contractService.FindByIds(ids.join(','));
    const items: ContractEntity[] = response?.data || [];

    return items.reduce((acc: Record<number, string>, item) => {
      acc[item.id] = this.formatContractLabel(item);
      return acc;
    }, {});
  }

  private formatContractLabel(contract: ContractEntity): string {
    const benefitName = typeof contract.benefit_id === 'object' && contract.benefit_id
      ? contract.benefit_id.name
      : contract.benefit_id;
    const carrierName = contract.carrier_id?.xipp_commercial_name;
    const ownerName = contract.contract_owner_id?.legal_name;

    return [benefitName, carrierName, contract.policy, ownerName]
      .filter((part) => !!part)
      .join(' - ') || String(contract.id);
  }

  private buildListQueryParams(params: BiPanelRuleListParams): Record<string, string | number | boolean> {
    const { filter } = params;
    const query: Record<string, string | number | boolean> = {
      page: params.page,
      size: params.size,
    };

    if (filter.carrierIds.length) {
      query.level = BI_PANEL_RULE_LIST_LEVEL;
      query.carrierIds = filter.carrierIds.join(',');
    }
    if (filter.financialGroupId) query.financialGroupId = filter.financialGroupId;
    if (filter.contractIds.length) query.contractIds = filter.contractIds.join(',');
    if (filter.paymentModels.length) query.paymentModels = filter.paymentModels.join(',');
    if (filter.active !== null) query.active = filter.active;

    return query;
  }
}
